/**
 * @module lib/performance/user-timing
 * @description The Performance interface provides access to performance-related information for the current page.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Performance
 * @see https://www.html5rocks.com/en/tutorials/webperformance/usertiming/
 * @example
 * mark("something_started");
 * mark("something_done");
 * measure("something");
 * logEntriesByName("something");
 */
import logger from "utils/logger";

const perf = window.performance || window.msPerformance || window.webkitPerformance || window.mozPerformance;
const debugTag = "[TIMING]"; // console namespace

/**
 * @method mark
 * @param {String} name - of the performance name to log
 * @returns {String}
 */
export function mark(name) {
  if (perf && "mark" in perf) {
    return perf.mark(`tgam.${name}`);
  }
}

/**
 * @method measure
 * @param {String} name - of the performance name to log
 * @param {String} start - value to append to name, for measure's starting mark [optional]
 * @param {String} done - value to append to name, for measure's ending mark [optional]
 */
export function measure(name, start = "_started", done = "_done") {
  if (perf && "measure" in perf) {
    perf.measure(`tgam.${name}`, `tgam.${name}${start}`, `tgam.${name}${done}`);
  }
}

/**
 * @method getEntriesByName
 * @param {String} name - of the performance entry to retrieve
 * @param {String} type - of the entry to retrieve.
 * The valid entry types are listed in PerformanceEntry.entryType.
 * @returns {Array<PerformanceEntry>} of the performance entries
 */
export function getEntriesByName(name, type = "measure") {
  let items = [];
  if (perf && "getEntriesByName" in perf) {
    items = perf.getEntriesByName(`tgam.${name}`, type);
  }
  return items;
}

/**
 * @method logEntriesByName
 * @param {String} name - of the performance entry to log
 * @param {String} type - of the entry to retrieve.
 * The valid entry types are listed in PerformanceEntry.entryType.
 */
export function logEntriesByName(name, type = "measure") {
  getEntriesByName(name, type).forEach(i => {
    logger.info(`${debugTag} ${i.name}: ${i.duration}`);
  });
}
